import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { isNoSignInPreviewActive } from '../debug'
import { DEBUG_MEMBER_FORM_DEFINITIONS } from '../services/formRequestService'
import {
  FORM_PAGE_SIZE,
  definitionFromSnapshot,
  subscribeRestrictedFormDefinitions,
} from '../services/formsService'
import { normalizeFormTitle, type FormDefinition, type FormLifecycleStatus, type FormResponse, type FormSort } from '../types/forms'
import { getDemoWorkspaceData } from '../explore_seeds/ariaAndCoDemoData'
import { useAccessRestriction } from './useAccessRestriction'
import { firestoreTimeToMillis } from '../utils/firestoreTime'

export type FormDefinitionCursor = QueryDocumentSnapshot<DocumentData> | null

export type FormPageState = {
  key?: string
  items: FormDefinition[]
  nextCursor: FormDefinitionCursor
  hasMore: boolean
  loading: boolean
  error: Error | null
  loadMore: () => void | Promise<void>
}

const isPermissionDeniedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const code = (error as { code?: unknown }).code
  if (typeof code === 'string' && code.includes('permission-denied')) return true
  const message = (error as { message?: unknown }).message
  if (typeof message === 'string' && message.includes('permission-denied')) return true
  return false
}

const applyFiltersAndSort = (
  list: FormDefinition[],
  lifecycleStatus: FormLifecycleStatus | 'ALL',
  searchPrefix: string,
  sort: FormSort,
): FormDefinition[] => {
  let filtered = list
  if (lifecycleStatus !== 'ALL') {
    filtered = filtered.filter((f) => f.lifecycleStatus === lifecycleStatus)
  }
  if (searchPrefix) {
    filtered = filtered.filter((f) => Array.isArray(f.titlePrefixes) && f.titlePrefixes.includes(searchPrefix))
  }
  const sorted = [...filtered]
  if (sort === 'TITLE') {
    sorted.sort((a, b) => {
      const tA = (a.titleSearch ?? a.title ?? '').toLowerCase()
      const tB = (b.titleSearch ?? b.title ?? '').toLowerCase()
      if (tA !== tB) return tA.localeCompare(tB)
      return a.id.localeCompare(b.id)
    })
  } else {
    const isOldest = sort === 'OLDEST'
    sorted.sort((a, b) => {
      const timeA = firestoreTimeToMillis(a.updatedAt)
      const timeB = firestoreTimeToMillis(b.updatedAt)
      if (timeA !== timeB) return isOldest ? timeA - timeB : timeB - timeA
      return isOldest ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)
    })
  }
  return sorted
}

export const useFormDefinitions = (input: {
  workspaceId: string
  lifecycleStatus: FormLifecycleStatus | 'ALL'
  search: string
  sort: FormSort
  cursor: FormDefinitionCursor
}): FormPageState => {
  const debugActive = isNoSignInPreviewActive()
  const { restricted: isRestricted, uid } = useAccessRestriction()

  const normalizedSearch = normalizeFormTitle(input.search)
  const key = `${input.workspaceId}|${input.lifecycleStatus}|${normalizedSearch}|${input.sort}|${input.cursor?.id ?? 'first'}|${isRestricted ? `restricted:${uid}` : 'unrestricted'}`
  const [state, setState] = useState<Omit<FormPageState, 'loadMore'>>({ key: '', items: [], nextCursor: null, hasMore: false, loading: true, error: null })
  const [restrictedRawItems, setRestrictedRawItems] = useState<FormDefinition[] | null>(null)
  const [restrictedPageLimit, setRestrictedPageLimit] = useState(FORM_PAGE_SIZE)

  const scopeKey = `${input.workspaceId}|${uid}|${isRestricted}`
  const [prevScopeKey, setPrevScopeKey] = useState(scopeKey)
  if (prevScopeKey !== scopeKey) {
    setPrevScopeKey(scopeKey)
    setRestrictedRawItems(null)
  }

  useEffect(() => {
    setRestrictedPageLimit(FORM_PAGE_SIZE)
  }, [input.lifecycleStatus, normalizedSearch, input.sort, input.workspaceId, uid])

  const loadMore = useCallback(async () => {
    if (isRestricted) {
      if (!state.hasMore || state.loading) return
      setRestrictedPageLimit((prev) => prev + FORM_PAGE_SIZE)
      return
    }
    if (!state.hasMore || !state.nextCursor || state.loading) return
    const currentCursor = state.nextCursor
    const constraints: QueryConstraint[] = [where('workspaceId', '==', input.workspaceId)]
    if (input.lifecycleStatus !== 'ALL') constraints.push(where('lifecycleStatus', '==', input.lifecycleStatus))
    if (normalizedSearch) constraints.push(where('titlePrefixes', 'array-contains', normalizedSearch))
    if (input.sort === 'TITLE') constraints.push(orderBy('titleSearch', 'asc'), orderBy(documentId(), 'asc'))
    else {
      const direction = input.sort === 'OLDEST' ? 'asc' : 'desc'
      constraints.push(orderBy('updatedAt', direction), orderBy(documentId(), direction))
    }
    constraints.push(startAfter(currentCursor))
    constraints.push(limit(FORM_PAGE_SIZE + 1))

    try {
      const snapshot = await getDocs(query(collection(firestoreDb, 'form_definitions'), ...constraints))
      const visible = snapshot.docs.slice(0, FORM_PAGE_SIZE)
      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...visible.map(definitionFromSnapshot)],
        nextCursor: visible.at(-1) ?? null,
        hasMore: snapshot.size > FORM_PAGE_SIZE,
      }))
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error : new Error(String(error)),
      }))
    }
  }, [input.lifecycleStatus, input.sort, input.workspaceId, isRestricted, normalizedSearch, state.hasMore, state.loading, state.nextCursor])

  useEffect(() => {
    if (debugActive) {
      setState({
        key,
        items: DEBUG_MEMBER_FORM_DEFINITIONS.filter((form) => input.lifecycleStatus === 'ALL' || form.lifecycleStatus === input.lifecycleStatus),
        nextCursor: null,
        hasMore: false,
        loading: false,
        error: null,
      })
      return
    }
    const demo = getDemoWorkspaceData(input.workspaceId)
    if (demo) {
      setState({
        key,
        items: demo.forms.filter((form) => input.lifecycleStatus === 'ALL' || form.lifecycleStatus === input.lifecycleStatus),
        nextCursor: null,
        hasMore: false,
        loading: false,
        error: null,
      })
      return
    }
    if (!input.workspaceId) return

    if (isRestricted) {
      setRestrictedRawItems(null)
      if (!uid) {
        setState({ key, items: [], nextCursor: null, hasMore: false, loading: false, error: null })
        return
      }
      let isMounted = true
      const unsubscribe = subscribeRestrictedFormDefinitions(
        input.workspaceId,
        uid,
        (definitions) => {
          if (!isMounted) return
          setRestrictedRawItems(definitions)
        },
        (error) => {
          if (!isMounted) return
          setState({ key, items: [], nextCursor: null, hasMore: false, loading: false, error })
        },
      )
      return () => {
        isMounted = false
        unsubscribe()
      }
    }

    const constraints: QueryConstraint[] = [where('workspaceId', '==', input.workspaceId)]
    if (input.lifecycleStatus !== 'ALL') constraints.push(where('lifecycleStatus', '==', input.lifecycleStatus))
    if (normalizedSearch) constraints.push(where('titlePrefixes', 'array-contains', normalizedSearch))
    if (input.sort === 'TITLE') constraints.push(orderBy('titleSearch', 'asc'), orderBy(documentId(), 'asc'))
    else {
      const direction = input.sort === 'OLDEST' ? 'asc' : 'desc'
      constraints.push(orderBy('updatedAt', direction), orderBy(documentId(), direction))
    }
    if (input.cursor) constraints.push(startAfter(input.cursor))
    constraints.push(limit(FORM_PAGE_SIZE + 1))

    return onSnapshot(query(collection(firestoreDb, 'form_definitions'), ...constraints), (snapshot) => {
      const visible = snapshot.docs.slice(0, FORM_PAGE_SIZE)
      setState({
        key,
        items: visible.map(definitionFromSnapshot),
        nextCursor: visible.at(-1) ?? null,
        hasMore: snapshot.size > FORM_PAGE_SIZE,
        loading: false,
        error: null,
      })
    }, (error) => {
      setState({ key, items: [], nextCursor: null, hasMore: false, loading: false, error })
    })
  }, [debugActive, input.cursor, input.lifecycleStatus, input.sort, input.workspaceId, isRestricted, key, normalizedSearch, uid])

  useEffect(() => {
    if (!isRestricted) return
    if (!uid) {
      setState({ key, items: [], nextCursor: null, hasMore: false, loading: false, error: null })
      return
    }
    if (restrictedRawItems === null) {
      setState({ key, items: [], nextCursor: null, hasMore: false, loading: true, error: null })
      return
    }
    const sorted = applyFiltersAndSort(restrictedRawItems, input.lifecycleStatus, normalizedSearch, input.sort)
    const visible = sorted.slice(0, restrictedPageLimit)
    const hasMore = sorted.length > restrictedPageLimit
    setState({
      key,
      items: visible,
      nextCursor: null,
      hasMore,
      loading: false,
      error: null,
    })
  }, [isRestricted, restrictedRawItems, input.lifecycleStatus, normalizedSearch, input.sort, restrictedPageLimit, key, uid])

  if (isRestricted && !uid) return { items: [], nextCursor: null, hasMore: false, loading: false, error: null, loadMore: () => {} }
  if (state.key !== key) return { items: [], nextCursor: null, hasMore: false, loading: true, error: null, loadMore: () => {} }
  return { ...state, loadMore }
}

export const useFormDefinitionsByIds = (formIds: readonly string[]) => {
  const debugActive = isNoSignInPreviewActive()
  const { restricted: isRestricted } = useAccessRestriction()
  const key = useMemo(
    () => Array.from(new Set(formIds.filter((formId): formId is string => typeof formId === 'string' && formId.trim().length > 0))).join('|'),
    [formIds],
  )
  const normalizedIds = useMemo(
    () => (key ? key.split('|') : []),
    [key],
  )
  const [state, setState] = useState<{ key: string; items: FormDefinition[]; loading: boolean; error: Error | null }>({
    key: '',
    items: [],
    loading: normalizedIds.length > 0,
    error: null,
  })

  useEffect(() => {
    if (debugActive || normalizedIds.length === 0) {
      return
    }
    const forms = new Map<string, FormDefinition>()
    const missing = new Set(normalizedIds)
    const unsubscribe = normalizedIds.map((formId) => onSnapshot(
      doc(firestoreDb, 'form_definitions', formId),
      (snapshot) => {
        if (snapshot.exists()) forms.set(formId, definitionFromSnapshot(snapshot as QueryDocumentSnapshot<DocumentData>))
        else forms.delete(formId)
        missing.delete(formId)
        setState({
          key,
          items: normalizedIds.flatMap((id) => {
            const form = forms.get(id)
            return form ? [form] : []
          }),
          loading: missing.size > 0,
          error: null,
        })
      },
      (error) => {
        if (isRestricted && isPermissionDeniedError(error)) {
          forms.delete(formId)
          missing.delete(formId)
          setState({
            key,
            items: normalizedIds.flatMap((id) => {
              const form = forms.get(id)
              return form ? [form] : []
            }),
            loading: missing.size > 0,
            error: null,
          })
          return
        }
        setState({
          key,
          items: normalizedIds.flatMap((id) => {
            const form = forms.get(id)
            return form ? [form] : []
          }),
          loading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        })
      },
    ))
    return () => unsubscribe.forEach((stop) => stop())
  }, [debugActive, isRestricted, key, normalizedIds])

  if (debugActive) {
    return {
      items: normalizedIds.flatMap((formId) => {
        const form = DEBUG_MEMBER_FORM_DEFINITIONS.find((entry) => entry.id === formId)
        return form ? [form] : []
      }),
      loading: false,
      error: null,
    }
  }
  if (normalizedIds.length === 0) return { items: [], loading: false, error: null }
  if (state.key !== key) return { items: [], loading: normalizedIds.length > 0, error: null }
  return state
}

export const useFormDefinition = (formId: string | null) => {
  const debugActive = isNoSignInPreviewActive()
  const { restricted: isRestricted } = useAccessRestriction()
  const key = formId ?? ''
  const [state, setState] = useState<{ key: string; form: FormDefinition | null; loading: boolean; error: Error | null }>({ key: '', form: null, loading: Boolean(formId), error: null })
  useEffect(() => {
    if (debugActive || !formId) return
    return onSnapshot(doc(firestoreDb, 'form_definitions', formId), (snapshot) => {
      const form = snapshot.exists()
        ? definitionFromSnapshot(snapshot as QueryDocumentSnapshot<DocumentData>)
        : null
      setState({ key: formId, form, loading: false, error: null })
    }, (error) => {
      if (isRestricted && isPermissionDeniedError(error)) {
        setState({ key: formId, form: null, loading: false, error: null })
        return
      }
      setState({ key: formId, form: null, loading: false, error: error instanceof Error ? error : new Error(String(error)) })
    })
  }, [debugActive, formId, isRestricted, key])
  if (debugActive) {
    return { form: DEBUG_MEMBER_FORM_DEFINITIONS.find((form) => form.id === formId) ?? null, loading: false, error: null }
  }
  if (!formId) return { form: null, loading: false, error: null }
  if (state.key !== key) return { form: null, loading: true, error: null }
  return state
}

export const useLatestFormResponse = (workspaceId: string, formId: string | null, submittedBy?: string | null) => {
  const key = `${workspaceId}:${formId ?? ''}:${submittedBy ?? 'all'}`
  const [state, setState] = useState<{ key: string; response: FormResponse | null; loading: boolean }>({ key: '', response: null, loading: false })
  const demo = getDemoWorkspaceData(workspaceId)
  const responseQuery = useMemo(() => {
    if (!workspaceId || !formId || (demo && formId)) return null
    const constraints = [
      where('workspaceId', '==', workspaceId),
      where('formId', '==', formId),
      ...(submittedBy ? [where('submittedBy', '==', submittedBy)] : []),
      orderBy('submittedAt', 'desc'),
      limit(1),
    ]
    return query(
      collection(firestoreDb, 'form_responses'),
      ...constraints,
    )
  }, [demo, formId, submittedBy, workspaceId])
  useEffect(() => {
    if (demo && formId) {
      return
    }
    if (!responseQuery) return
    return onSnapshot(responseQuery, (snapshot) => {
      const entry = snapshot.docs[0]
      setState({ key, response: entry ? ({ id: entry.id, ...entry.data() } as FormResponse) : null, loading: false })
    }, () => setState({ key, response: null, loading: false }))
  }, [demo, formId, key, responseQuery])
  if (demo && formId) {
    const responses = demo.formResponses[formId] ?? []
    const filtered = submittedBy ? responses.filter((r) => r.submittedBy === submittedBy) : responses
    return { response: filtered[0] ?? null, loading: false }
  }
  if (!responseQuery) return { response: null, loading: false }
  if (state.key !== key) return { response: null, loading: true }
  return state
}
